<script setup lang="ts">
import { nextTick, onMounted, ref, watch } from 'vue'
import { AlertTriangle, PlusSquare, Send } from 'lucide-vue-next'
import { Button } from '@/components/ui/button'
import { Textarea } from '@/components/ui/textarea'
import { Card } from '@/components/ui/card'
import {
  type ChartConfig,
  type DonutRendererConfig,
  type MapRendererConfig,
  type MetricRendererConfig,
  type MultiLineRendererConfig,
  type TableRendererConfig,
  useQueryChatStore,
} from '@/stores/queryChat'
import { storeToRefs } from 'pinia'
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert'

import ChartRenderer from '@/components/query/ChartRenderer.vue'
import MapRenderer from '@/components/query/MapRenderer.vue'
import MetricRenderer from '@/components/query/MetricRenderer.vue'
import DonutRenderer from '@/components/query/DonutRenderer.vue'
import MultiLineRenderer from '@/components/query/MultiLineRenderer.vue'
import TableRenderer from '@/components/query/TableRenderer.vue'

const userInput = ref('')
const textareaRef = ref<HTMLTextAreaElement | null>(null)
const messagesContainer = ref<HTMLDivElement | null>(null)

const chatStore = useQueryChatStore()
const { currentChat, loading, showSessionAlert } = storeToRefs(chatStore)

const handleSubmit = async (event?: Event) => {
  if (event) event.preventDefault()
  const query = userInput.value.trim()
  if (!query) return

  userInput.value = ''
  await chatStore.sendMessage(query)
}

const handleKeydown = (event: KeyboardEvent) => {
  if (event.key === 'Enter' && !event.shiftKey) {
    event.preventDefault()
    handleSubmit()
  }
}

const startNewChat = () => {
  chatStore.startNewChat()
}

const scrollToBottom = async () => {
  await nextTick()
  if (messagesContainer.value) {
    messagesContainer.value.scrollTo({
      top: messagesContainer.value.scrollHeight,
      behavior: 'smooth',
    })
  }
}

watch(
  () => [currentChat.value?.id, currentChat.value?.messages.length],
  () => {
    scrollToBottom()
  },
)

onMounted(() => {
  chatStore.loadChatFromLocalStorage()
  scrollToBottom()
})
</script>

<template>
  <div class="relative w-full h-[calc(100vh-5rem)] flex flex-col overflow-hidden">
    <div ref="messagesContainer" class="overflow-y-auto pb-40 px-4 pt-2">
      <div class="max-w-4xl mx-auto w-full">
        <Alert
          v-if="showSessionAlert"
          variant="default"
          class="bg-yellow-100 border-yellow-300 text-yellow-800 max-w-xl mx-auto text-center mb-10"
        >
          <AlertTitle class="flex flex-col items-center justify-center gap-1.5"
            ><AlertTriangle class="h-4 w-4" />Session Only</AlertTitle
          >
          <AlertDescription>
            Chat history is maintained for the current session only and will be lost when the
            session ends.
          </AlertDescription>
        </Alert>
        <div v-if="currentChat">
          <div v-for="(message, index) in currentChat.messages" :key="index" class="my-4">
            <div v-if="message.type === 'user'" class="flex justify-end">
              <Card class="bg-blue-100 p-4 max-w-2xl">{{ message.content }}</Card>
            </div>

            <div v-else-if="message.type === 'response'" class="flex justify-start">
              <Card class="p-4 max-w-4xl w-full">
                <div v-if="message.content.chart_type === 'metric'">
                  <MetricRenderer :config="message.content.config as MetricRendererConfig" />
                </div>

                <div
                  v-else-if="
                    message.content.chart_type === 'map' || message.content.chart_type === 'heatmap'
                  "
                  class="w-full h-[70vh]"
                >
                  <MapRenderer
                    :config="message.content.config as MapRendererConfig"
                    :chart-type="message.content.chart_type"
                  />
                </div>

                <div v-else-if="message.content.chart_type === 'table'">
                  <TableRenderer :config="message.content.config as TableRendererConfig" />
                </div>

                <div v-else-if="message.content.chart_type === 'error'">
                  <Alert variant="destructive">
                    <AlertTriangle class="h-4 w-4" />
                    <AlertTitle>Error</AlertTitle>
                    <AlertDescription>{{ message.content.config }}</AlertDescription>
                  </Alert>
                </div>

                <div v-else-if="message.content.chart_type === 'multi'">
                  <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div
                      v-for="(chart, chartIndex) in (message.content.config as ChartConfig).charts"
                      :key="chartIndex"
                      class="border rounded-lg p-2"
                    >
                      <h4 class="font-semibold text-center mb-2">{{ chart.title }}</h4>
                      <ChartRenderer :config="chart.config" />
                    </div>
                  </div>
                </div>

                <div v-else-if="message.content.chart_type === 'donut'">
                  <DonutRenderer :config="message.content.config as DonutRendererConfig" />
                </div>

                <div v-else-if="message.content.chart_type === 'multiline'">
                  <MultiLineRenderer :config="message.content.config as MultiLineRendererConfig" />
                </div>

                <div v-else>
                  <ChartRenderer :config="message.content.config" />
                </div>
              </Card>
            </div>
          </div>
          <div v-if="loading" class="flex justify-start">
            <Card class="bg-gray-100 p-4 flex items-center space-x-2 mb-14">
              <div class="typing-indicator">
                <span></span>
                <span></span>
                <span></span>
              </div>
            </Card>
          </div>
        </div>
        <div v-else class="text-center h-full flex flex-col justify-center min-h-[50vh]">
          <h2 class="text-2xl font-semibold text-muted-foreground">Query Beta Interface</h2>
          <p class="text-muted-foreground">Start a new chat to begin.</p>
        </div>
      </div>
    </div>

    <div class="absolute bottom-10 left-0 w-full z-10 p-4 bg-background/80 backdrop-blur-sm">
      <div class="max-w-3xl mx-auto">
        <div class="flex justify-between mb-2">
          <div>
            <Button @click="startNewChat" variant="outline">
              <PlusSquare class="h-4 w-4 mr-2" />
              New Chat
            </Button>
          </div>
        </div>
        <form @submit="handleSubmit" class="relative">
          <Textarea
            ref="textareaRef"
            id="query"
            :rows="1"
            v-model="userInput"
            @keydown="handleKeydown"
            class="w-full text-base pr-12 py-3 pl-4 resize-none"
            placeholder="What do you want to visualize today?"
            required
            :disabled="loading"
          />
          <Button
            type="submit"
            size="icon"
            class="absolute right-3 top-1/2 -translate-y-1/2 h-8 w-8"
            :disabled="loading"
          >
            <Send class="h-4 w-4" />
          </Button>
        </form>
      </div>
    </div>
  </div>
</template>

<style scoped>
/* Il tuo CSS .typing-indicator rimane invariato */
.prose {
  max-width: none;
}

.typing-indicator {
  display: flex;
  align-items: center;
  justify-content: center;
}

.typing-indicator span {
  height: 8px;
  width: 8px;
  float: left;
  margin: 0 1px;
  background-color: #9e9ea1;
  display: block;
  border-radius: 50%;
  opacity: 0.4;
  animation: typing 1s infinite;
}

.typing-indicator span:nth-of-type(1) {
  animation-delay: 0s;
}

.typing-indicator span:nth-of-type(2) {
  animation-delay: 0.2s;
}

.typing-indicator span:nth-of-type(3) {
  animation-delay: 0.4s;
}

@keyframes typing {
  0% {
    opacity: 0.4;
    transform: translateY(0);
  }
  50% {
    opacity: 1;
    transform: translateY(-4px);
  }
  100% {
    opacity: 0.4;
    transform: translateY(0);
  }
}
</style>
